import { isNullOrUndefined } from "../../../../../../../../tools";
import { IdProvider } from "../../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../../services/utils.service";
import { DataContainer } from "../../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../../infrastructure/datacontainer.service";
import { RequestTypes } from "../../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../../infrastructure/validationresultaccumulator";
import {
  AshaPostMasterFetchRequest,
  AshaPostNameFetchRequest,
} from "./ashapostfetchrequest";

export class AshaPostMasterProps {
  public created_date: string = "";
  public updated_date: string = "";
  public created_by: number = 0;
  public updated_by: number = 0;
  public deleted_by: number = 0;

  public id: number = 0;

  public state_id: number = 0;
  public readonly state_name_en: string = "";
  public readonly state_name_mr: string = "";

  public zone_id: number = 0;
  public readonly zone_name: string = "";

  public district_id: number = 0;
  public readonly district_name_en: string = "";
  public readonly district_name_mr: string = "";

  public block_id: number = 0;
  public readonly block_name_en: string = "";
  public readonly block_name_mr: string = "";

  public phc_id: number = 0;
  public readonly phc_name: string = "";

  public bcm_post_id: number = 0;
  public readonly bcm_post_name: string = "";

  public sc_id: number = 0;
  public readonly sc_name: string = "";

  public village_id: number = 0;
  public readonly village_name_en: string = "";
  public readonly village_name_mr: string = "";

  public under_supervision: number = 0;
  public name: string = "";
  public population_covered_by_asha: number = 0;
  public month: number = 0;

  public asha_post_id: number = 0;
  public asha_post_number: number = 0;
  public financial_year_id: number = 0;
  public asha_employee_record_id: number = 0;
  public readonly is_newly_created: boolean = false;

  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new AshaPostMasterProps(true);
  }
}

export class AshaPostMaster implements IPersistable<AshaPostMaster> {
  public static readonly mastertablename: string = "asha_post";

  private constructor(
    public readonly p: AshaPostMasterProps,
    public readonly AllowEdit: boolean
  ) {}

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0)
        throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): AshaPostMaster {
    let newAshaPostMaster: AshaPostMasterProps = Utils.GetInstance().DeepCopy(
      this.p
    );
    return AshaPostMaster.CreateInstance(newAshaPostMaster, true);
  }

  public static CreateNewInstance() {
    return new AshaPostMaster(AshaPostMasterProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new AshaPostMaster(data as AshaPostMasterProps, allowEdit);
  }

  public CheckSaveValidity(
    _td: TransportData,
    vra: ValidationResultAccumulator
  ): void {
    if (!this.AllowEdit)
      vra.add("", "This object is not editable and hence cannot be saved.");
    if (!this.p.name || this.p.name.trim() == "") {
      vra.add("name", "AshaPostMaster Name cannot be blank. ");
    }
    if (this.p.state_id <= 0) vra.add("state_id", "State cannot be blank. ");
    if (this.p.zone_id <= 0) vra.add("zone_id", "Zone cannot be blank. ");
    if (this.p.district_id <= 0)
      vra.add("district_id", "District cannot be blank. ");
    if (this.p.block_id <= 0) vra.add("block_id", "Block cannot be blank. ");
    if (this.p.block_id <= 0)
      vra.add("block_id", "Block Name cannot be Blank.");

    // Validate Month (string but numeric)
    const monthNum = Number(this.p.month);
    if (!this.p.month || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      vra.add("month", "Please select a valid month.");
    }
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(
      td.MainData,
      AshaPostMaster.mastertablename,
      this.p
    );
  }

  private static m_currentInstance: AshaPostMaster =
    AshaPostMaster.CreateNewInstance();

  public static GetCurrentInstance() {
    return AshaPostMaster.m_currentInstance;
  }

  public static SetCurrentInstance(value: AshaPostMaster) {
    AshaPostMaster.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(
    td: TransportData
  ): AshaPostMaster {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, AshaPostMaster.mastertablename)) {
      for (let data of dcs.GetCollection(
        td.MainData,
        AshaPostMaster.mastertablename
      )!.Entries) {
        return AshaPostMaster.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(
    cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"
  ): AshaPostMaster[] {
    let result: AshaPostMaster[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, AshaPostMaster.mastertablename)) {
      let coll = dcs.GetCollection(cont, AshaPostMaster.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate))
        entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(AshaPostMaster.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): AshaPostMaster[] {
    return AshaPostMaster.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(
    req: AshaPostMasterFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(
      pktRequest
    );
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(
    id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AshaPostMasterFetchRequest();
    req.asha_post_master_ids.push(id);

    let tdResponse = (await AshaPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AshaPostMaster.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(
    req: AshaPostMasterFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let tdResponse = (await AshaPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AshaPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AshaPostMasterFetchRequest();
    let tdResponse = (await AshaPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AshaPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByZoneDistrictBlockPhcRef(
    zone_id: number,
    district_id: number,
    block_id: number,
    bcm_post_id: number,
    phc_id: number,
    sc_id: number,
    village_id: number,
    under_supervision: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AshaPostMasterFetchRequest();
    if (zone_id) {
      req.zone_ids.push(zone_id);
    }
    if (district_id) {
      req.district_ids.push(district_id);
    }
    if (block_id) {
      req.block_ids.push(block_id);
    }
      if (bcm_post_id) {
      req.bcm_post_ids.push(bcm_post_id);
    }
    if (phc_id) {
      req.phc_ids.push(phc_id);
    }
    if (sc_id) {
      req.sc_ids.push(sc_id);
    }
    if (village_id) {
      req.village_ids.push(village_id);
    }
    if (under_supervision) {
      req.under_supervision.push(under_supervision);
    }
    let tdResponse = (await AshaPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AshaPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListWithName(
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AshaPostNameFetchRequest();
    let tdResponse = (await AshaPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AshaPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByAFPostRef(
    af_post_id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AshaPostNameFetchRequest();
    if (af_post_id) {
      req.af_post_ids.push(af_post_id);
    }
    let tdResponse = (await AshaPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AshaPostMaster.ListFromTransportData(tdResponse);
  }

  public async DeleteInstance(
    successHandler: () => Promise<void> = null!,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(
      pktRequest
    );
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    } else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }
}
